// Polls and events (attachment menu), like WhatsApp's: create, vote / RSVP,
// see who answered. Everything travels end-to-end encrypted (engine.ts).
import { useState } from "preact/hooks";
import type { ChatEvent, Msg, RSVP } from "../db";
import { POLL_MAX_OPTIONS } from "../engine";
import * as S from "../state";
import { serverOrigin } from "../platform";
import { joinCall } from "./Call";
import { sharing } from "./chatui";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

const close = () => (sharing.value = null);
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const optionId = () => crypto.randomUUID().replace(/-/g, "").slice(0, 8);
const who = (jid: string) => (jid === S.myJid.value ? "You" : S.memberName(jid));

// ---- create a poll -------------------------------------------------------------------

export function PollDialog({ jid }: { jid: string }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<{ id: string; text: string }[]>(() => [
    { id: optionId(), text: "" },
    { id: optionId(), text: "" },
  ]);
  const [multi, setMulti] = useState(true);
  const filled = options.filter((o) => o.text.trim());
  const ok = question.trim() && filled.length >= 2;

  function setOption(i: number, text: string) {
    const next = options.map((o, j) => (j === i ? { ...o, text } : o));
    // Always one empty row at the end to type the next option into.
    if (next[next.length - 1].text.trim() && next.length < POLL_MAX_OPTIONS) next.push({ id: optionId(), text: "" });
    setOptions(next);
  }
  async function send() {
    if (!ok) return;
    close();
    await engine.value
      ?.sendDraft(jid, { body: "", kind: "poll", poll: { question: question.trim(), options: filled.map((o) => ({ id: o.id, text: o.text.trim() })), multi } })
      .catch((e) => toast(errText(e)));
  }
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal create-poll" role="dialog" aria-label="Create poll" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Create poll</h2>
        </header>
        <div class="form-body">
          <label class="form-label">Question</label>
          <input class="line-input" value={question} maxLength={300} onInput={(e) => setQuestion(e.currentTarget.value)} placeholder="Ask question" aria-label="Poll question" autoFocus />
          <label class="form-label">Options</label>
          {options.map((o, i) => (
            <input
              key={o.id}
              class="line-input"
              value={o.text}
              maxLength={100}
              onInput={(e) => setOption(i, e.currentTarget.value)}
              placeholder={i < 2 ? `Option ${i + 1}` : "+ Add"}
              aria-label={`Option ${i + 1}`}
            />
          ))}
          <label class="toggle">
            <span>
              <strong>Allow multiple answers</strong>
            </span>
            <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.currentTarget.checked)} aria-label="Allow multiple answers" />
          </label>
        </div>
        <footer class="forward-footer">
          <span class="ellipsis muted">{ok ? `${filled.length} options` : "Add a question and at least 2 options"}</span>
          <button class="icon-btn send-fab" disabled={!ok} onClick={() => void send()} aria-label="Send poll">
            <Icon name="send" />
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---- a poll in the chat ----------------------------------------------------------------

export function PollContent({ m }: { m: Msg }) {
  const p = m.poll!;
  const [voters, setVoters] = useState(false);
  const me = S.myJid.value;
  const votes = p.votes ?? {};
  const mine = new Set(votes[me]?.choices ?? []);
  const counts = new Map(p.options.map((o) => [o.id, 0]));
  for (const v of Object.values(votes)) for (const c of v.choices) counts.set(c, (counts.get(c) ?? 0) + 1);
  const max = Math.max(1, ...counts.values());
  const people = Object.values(votes).filter((v) => v.choices.length).length;

  function pick(id: string) {
    const next = p.multi ? (mine.has(id) ? [...mine].filter((x) => x !== id) : [...mine, id]) : mine.has(id) ? [] : [id];
    void engine.value?.vote(m, next).catch((e) => toast(errText(e)));
  }
  return (
    <div class="poll">
      <div class="poll-question">{p.question}</div>
      <div class="poll-hint muted small">
        <Icon name={p.multi ? "check" : "info"} size={14} /> {p.multi ? "Select one or more" : "Select one"}
      </div>
      <ul class="poll-options">
        {p.options.map((o) => {
          const n = counts.get(o.id) ?? 0;
          return (
            <li key={o.id}>
              <button class={"poll-option" + (mine.has(o.id) ? " chosen" : "")} onClick={() => pick(o.id)} aria-pressed={mine.has(o.id)} aria-label={`Vote ${o.text}`}>
                <span class={"poll-check" + (p.multi ? " multi" : "")}>{mine.has(o.id) && <Icon name="check" size={14} />}</span>
                <span class="poll-text">{o.text}</span>
                <span class="poll-count">{n}</span>
                <span class="poll-bar" style={{ width: `${(n / max) * 100}%` }} />
              </button>
            </li>
          );
        })}
      </ul>
      <button class="link poll-votes-btn" onClick={() => setVoters(true)} disabled={!people}>
        {people ? `View votes (${people})` : "No votes yet"}
      </button>
      {voters && (
        <AnswersDialog
          title="Poll details"
          heading={p.question}
          groups={p.options.map((o) => ({ label: o.text, people: Object.entries(votes).filter(([, v]) => v.choices.includes(o.id)).map(([j]) => j) }))}
          onClose={() => setVoters(false)}
        />
      )}
    </div>
  );
}

function AnswersDialog({ title, heading, groups, onClose }: { title: string; heading: string; groups: { label: string; people: string[] }[]; onClose: () => void }) {
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal answers" role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>{title}</h2>
        </header>
        <div class="form-body">
          <p class="answers-heading">{heading}</p>
          {groups.map((g) => (
            <section key={g.label} class="answers-group">
              <h3>
                {g.label} <span class="muted">{g.people.length}</span>
              </h3>
              {g.people.length ? (
                <ul>
                  {g.people.map((j) => (
                    <li key={j}>{who(j)}</li>
                  ))}
                </ul>
              ) : (
                <p class="muted small">No one</p>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- create an event -----------------------------------------------------------------

/** "2026-10-05T18:30" in local time for an <input type="datetime-local">. */
const localInput = (ms: number) => {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

export function EventDialog({ jid }: { jid: string }) {
  const nextHour = Math.ceil(Date.now() / 3_600_000) * 3_600_000;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState(localInput(nextHour));
  const [withEnd, setWithEnd] = useState(false);
  const [end, setEnd] = useState(localInput(nextHour + 3_600_000));
  const [place, setPlace] = useState("");
  const [call, setCall] = useState<"" | "voice" | "video">("");
  const [error, setError] = useState("");

  async function send() {
    const s = new Date(start).getTime();
    const e = withEnd ? new Date(end).getTime() : undefined;
    if (!name.trim()) return setError("Give the event a name.");
    if (!s) return setError("Pick when it starts.");
    if (e !== undefined && !(e > s)) return setError("The end has to be after the start.");
    close();
    const event: ChatEvent = { name: name.trim(), description: description.trim() || undefined, start: s, end: e, place: place.trim() || undefined, call: call || undefined };
    await engine.value?.sendDraft(jid, { body: "", kind: "event", event }).catch((err) => toast(errText(err)));
  }
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal create-event" role="dialog" aria-label="Create event" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Create event</h2>
        </header>
        <div class="form-body">
          <input class="line-input" value={name} maxLength={200} onInput={(e) => setName(e.currentTarget.value)} placeholder="Event name" aria-label="Event name" autoFocus />
          <textarea class="line-input" value={description} maxLength={2048} rows={2} onInput={(e) => setDescription(e.currentTarget.value)} placeholder="Description (optional)" aria-label="Event description" />
          <label class="form-label">Starts</label>
          <input class="line-input" type="datetime-local" value={start} onInput={(e) => setStart(e.currentTarget.value)} aria-label="Start date and time" />
          <label class="toggle">
            <span>
              <strong>End date and time</strong>
            </span>
            <input type="checkbox" checked={withEnd} onChange={(e) => setWithEnd(e.currentTarget.checked)} aria-label="Add end time" />
          </label>
          {withEnd && <input class="line-input" type="datetime-local" value={end} onInput={(e) => setEnd(e.currentTarget.value)} aria-label="End date and time" />}
          <input class="line-input" value={place} maxLength={300} onInput={(e) => setPlace(e.currentTarget.value)} placeholder="Location (optional)" aria-label="Event location" />
          <label class="form-label">Call link</label>
          <div class="seg" role="radiogroup" aria-label="Call link">
            {(["", "voice", "video"] as const).map((c) => (
              <button key={c} type="button" role="radio" aria-checked={call === c} class={"seg-btn" + (call === c ? " on" : "")} onClick={() => setCall(c)}>
                {c === "" ? "None" : c === "voice" ? "Voice call" : "Video call"}
              </button>
            ))}
          </div>
          {error && <p class="error">{error}</p>}
        </div>
        <footer class="forward-footer">
          <span class="ellipsis muted">{S.isGroup(jid) ? "Members can respond: going, maybe or can't go" : "They can respond: going, maybe or can't go"}</span>
          <button class="icon-btn send-fab" onClick={() => void send()} aria-label="Send event">
            <Icon name="send" />
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---- an event in the chat ----------------------------------------------------------------

const ANSWERS: { answer: RSVP; label: string }[] = [
  { answer: "going", label: "Going" },
  { answer: "maybe", label: "Maybe" },
  { answer: "no", label: "Can't go" },
];

function when(e: ChatEvent) {
  const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: new Date(ms).getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
  const time = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (!e.end) return `${day(e.start)} · ${time(e.start)}`;
  return new Date(e.start).toDateString() === new Date(e.end).toDateString() ? `${day(e.start)} · ${time(e.start)} – ${time(e.end)}` : `${day(e.start)} ${time(e.start)} – ${day(e.end)} ${time(e.end)}`;
}

/** An .ics file for the user's calendar app. */
function icsFor(m: Msg) {
  const e = m.event!;
  const stamp = (ms: number) => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const esc = (s: string) => s.replace(/[\\;,]/g, (c) => "\\" + c).replace(/\n/g, "\\n");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//xmm//events//EN",
    "BEGIN:VEVENT",
    `UID:${m.id}@xmm`,
    `DTSTAMP:${stamp(m.ts)}`,
    `DTSTART:${stamp(e.start)}`,
    `DTEND:${stamp(e.end ?? e.start + 3_600_000)}`,
    `SUMMARY:${esc(e.name)}`,
    e.description ? `DESCRIPTION:${esc(e.description)}` : "",
    e.place ? `LOCATION:${esc(e.place)}` : "",
    e.canceled ? "STATUS:CANCELLED" : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  return lines.join("\r\n") + "\r\n";
}

function addToCalendar(m: Msg) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([icsFor(m)], { type: "text/calendar" }));
  a.download = (m.event!.name.replace(/[^\w -]+/g, "").trim() || "event") + ".ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
}

/** A link that opens the chat and joins (or starts) its call. */
export function callLink(chat: string, media: "voice" | "video") {
  return `${serverOrigin()}/?call=${encodeURIComponent(chat)}&media=${media}`;
}

function startsIn(ms: number) {
  const min = Math.round((ms - Date.now()) / 60_000);
  if (min < 60) return `in ${Math.max(1, min)} min`;
  if (min < 24 * 60) return `in ${Math.round(min / 60)} h`;
  return new Date(ms).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export function EventContent({ m }: { m: Msg }) {
  const e = m.event!;
  const [guests, setGuests] = useState(false);
  const me = S.myJid.value;
  const rsvps = e.rsvps ?? {};
  const mine = rsvps[me]?.answer;
  const going = Object.values(rsvps).filter((r) => r.answer === "going").length;
  const past = (e.end ?? e.start + 3_600_000) < Date.now();
  // The call link works any time until the event ends (or is canceled).
  const callable = !!e.call && !e.canceled && !past;
  const link = e.call ? callLink(m.chat, e.call) : "";
  const join = () => callable && joinCall(m.chat, e.call === "video" ? "video" : "audio");
  const copy = () =>
    void navigator.clipboard?.writeText(link).then(
      () => toast("Call link copied"),
      () => toast(link),
    );
  const answer = (a: RSVP) => void engine.value?.rsvp(m, a).catch((err) => toast(errText(err)));
  return (
    <div class={"event-card" + (e.canceled ? " canceled" : "")}>
      <div class="event-head">
        <span class="event-icon">
          <Icon name="event" size={22} />
        </span>
        <div class="grow">
          <div class="event-name">{e.name}</div>
          <div class="event-when small">{e.canceled ? "Canceled" : when(e)}</div>
        </div>
      </div>
      {e.description && <p class="event-desc">{e.description}</p>}
      {e.place && (
        <div class="event-line small">
          <Icon name="pin" size={14} /> {e.place}
        </div>
      )}
      {e.call && (
        <div class={"event-call" + (callable ? "" : " off")}>
          <div class="event-call-head">
            <span class="event-call-icon">
              <Icon name={e.call === "video" ? "videocam" : "phone"} size={20} />
            </span>
            <div class="grow">
              <div class="event-call-title">{e.call === "video" ? "Video call link" : "Voice call link"}</div>
              <a class="event-call-url small" href={link} onClick={(ev) => (ev.preventDefault(), join())}>
                {link.replace(/^https?:\/\//, "")}
              </a>
            </div>
          </div>
          <div class="event-call-actions">
            <button class="primary event-join" disabled={!callable} onClick={join} aria-label={`Join ${e.call} call`}>
              <Icon name={e.call === "video" ? "videocam" : "phone"} size={16} /> Join call
            </button>
            <button class="chip" onClick={copy} aria-label="Copy call link">
              <Icon name="copy" size={14} /> Copy link
            </button>
          </div>
          <small class="muted event-call-note">
            {e.canceled ? "The event was canceled" : past ? "The event has ended" : e.start > Date.now() ? `You can join any time · starts ${startsIn(e.start)}` : "Happening now"}
          </small>
        </div>
      )}
      {!e.canceled && (
        <div class="event-rsvp" role="group" aria-label="Your response">
          {ANSWERS.map((a) => (
            <button key={a.answer} class={"chip" + (mine === a.answer ? " on" : "")} aria-pressed={mine === a.answer} onClick={() => answer(a.answer)}>
              {mine === a.answer && <Icon name="check" size={14} />} {a.label}
            </button>
          ))}
        </div>
      )}
      <div class="event-actions">
        <button class="link" onClick={() => setGuests(true)}>
          {going ? `${going} going` : "See responses"}
        </button>
        {!e.canceled && (
          <button class="link" onClick={() => addToCalendar(m)}>
            Add to calendar
          </button>
        )}
        {!e.canceled && m.dir === "out" && !past && (
          <button class="link danger" onClick={() => void engine.value?.cancelEvent(m).then(() => toast("Event canceled"), (err) => toast(errText(err)))}>
            Cancel event
          </button>
        )}
      </div>
      {guests && (
        <AnswersDialog
          title="Event responses"
          heading={e.name}
          groups={ANSWERS.map((a) => ({ label: a.label, people: Object.entries(rsvps).filter(([, r]) => r.answer === a.answer).map(([j]) => j) }))}
          onClose={() => setGuests(false)}
        />
      )}
    </div>
  );
}
